// ArcStageSelectorMolecule.tsx - Redesigned with dropdown-first approach

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import StageDropdownAtom from '../../atoms/StageDropdown/StageDropdownAtom';
import { Sphere } from 'types/global';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useTranslation } from 'localization';
import ArcActionButtonMolecule from '../ArcActionButton/ArcActionButtonMolecule';
import TransitionCommentDialogMolecule from '../TransitionCommentDialog/TransitionCommentDialogMolecule';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import {
  fetchArcTemplate,
  selectArcTemplate,
  selectArcTemplateLoading,
  selectArcTemplateError,
} from '@center-pwa/reducers/arcTemplatesReducer';
import './ArcStageSelector.scss';

interface StageInstance {
  sphereId: string;
  stageInstanceId: string;
  stageDefinitionId: string;
  arcId: string;
  status: 'active' | 'not_started' | 'completed';
  startedAt: string | null;
  completedAt: string | null;
}

export interface ArcStageSelectorMoleculeProps {
  arc: Sphere;
  onStageUpdated?: (updatedFlow: Sphere) => void;
  onAction?: (actionId: string, currentStageId: string) => void;
  disabled?: boolean;
  permissionCheck?: () => boolean | Promise<boolean>;
}

const getScalar = (x: any): string => Array.isArray(x) ? String(x[0]) : String(x);

const ArcStageSelectorMolecule: React.FC<ArcStageSelectorMoleculeProps> = ({
  arc,
  onStageUpdated,
  onAction,
  disabled = false,
  permissionCheck,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();

  const [stageInstances, setStageInstances] = useState<StageInstance[]>([]);
  const [stageFetchLoading, setStageFetchLoading] = useState(false);
  const [stageFetchError, setStageFetchError] = useState<string | null>(null);
  const [commentDialogVisible, setCommentDialogVisible] = useState(false);
  const [pendingTransitionTarget, setPendingTransitionTarget] = useState<string | null>(null);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const arcId = arc.id;
  const templateName = getScalar(arc.properties.templateSystemName);

  // Get arc template from Redux store
  const arcTemplate = useSelector((state: RootState) => selectArcTemplate(state, templateName));
  const templateLoading = useSelector((state: RootState) => selectArcTemplateLoading(state, templateName));
  const templateError = useSelector((state: RootState) => selectArcTemplateError(state, templateName));

  // Check if stages are already included in the arc data (from server expansion)
  const hasPreloadedStages = arc.stages && Array.isArray(arc.stages) && arc.stages.length > 0;

  const currentStageId = useMemo(() => {
    const active = stageInstances.find(si => si.status === 'active');
    return active ? active.stageDefinitionId : '';
  }, [stageInstances]);

  const healthStatus = useMemo(() => {
    return (arc.properties.healthStatus || 'on_track') as string;
  }, [arc.properties.healthStatus]);


  // 1️⃣ Fetch arc template from Redux (will use cache if available)
  useEffect(() => {
    if (!apiClients) return;
    const { schemaClient } = apiClients;

    // Only fetch if not already cached or loading
    if (!arcTemplate && !templateLoading) {
      dispatch(fetchArcTemplate({ systemName: templateName, apiClient: schemaClient }));
    }
  }, [templateName, apiClients, arcTemplate, templateLoading, dispatch]);

  // 2️⃣ Initialize from preloaded stages or fetch if needed
  const fetchStageInstances = useCallback(async () => {
    // If we have preloaded stages, use them directly
    if (hasPreloadedStages) {
      setStageInstances(arc.stages as StageInstance[]);
      return;
    }

    // Otherwise fetch from API (backward compatibility)
    if (!apiClients) return;
    const { graphClient } = apiClients;

    try {
      setStageFetchLoading(true);
      setStageFetchError(null);
      const { status, stages } = await graphClient.get<{
        status: string;
        stages: StageInstance[];
      }>(`/arcs/${arcId}/stages`);

      if (status === 'success') {
        setStageInstances(stages);
      } else {
        setStageFetchError(t('arc.errors.stages_not_found'));
      }
    } catch (error) {
      setStageFetchError(t('arc.errors.stages_not_found'));
      console.error('Failed to fetch stage instances:', error);
    } finally {
      setStageFetchLoading(false);
    }
  }, [arcId, apiClients, t, hasPreloadedStages, arc.stages]);

  useEffect(() => {
    fetchStageInstances();
  }, [fetchStageInstances]);

  const currentStageDef = useMemo(() => {
    if (!arcTemplate || !currentStageId) return null;
    return arcTemplate.definition.stages.find(s => s.id === currentStageId);
  }, [arcTemplate, currentStageId]);

  const currentStageName = useMemo(() => {
    if (!currentStageDef) return t('arc.ui.no_active_stage');
    return t(currentStageDef.localizationKey, currentStageDef.fallbackName);
  }, [currentStageDef, t]);

  const pendingTransitionStage = useMemo(() => {
    if (!pendingTransitionTarget || !arcTemplate) return null;
    return arcTemplate?.definition.stages.find(s => s.id === pendingTransitionTarget);
  }, [pendingTransitionTarget, arcTemplate]);

  const requireComment = useMemo(() => {
    const currentStageDef = arcTemplate?.definition.stages.find(s => s.id === currentStageId);
    return currentStageDef?.transition?.requireComment ?? false;
  }, [arcTemplate, currentStageId]);

  const handleTransition = async (newStageId: string, comment?: string) => {
    if (!apiClients) return;
    const { graphClient } = apiClients;
    try {
      setTransitionLoading(true);
      setTransitionError(null);
      const response = await graphClient.patch(`/arcs/${arcId}/transition`, {
        newStageId,
        comment,
      });

      if (response.status === 'success') {
        // Refetch stage instances to get updated status
        await fetchStageInstances();

        // Notify parent component with updated flow
        onStageUpdated?.(response.arc);

        setCommentDialogVisible(false);
        setPendingTransitionTarget(null);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('arc.errors.transition_failed');
      setTransitionError(errorMessage);
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleRequestComment = (targetStageId: string) => {
    setPendingTransitionTarget(targetStageId);
    setCommentDialogVisible(true);
  };

  const handleCommentConfirm = (comment?: string) => {
    if (pendingTransitionTarget) {
      handleTransition(pendingTransitionTarget, comment);
    }
  };

  const handleCommentCancel = () => {
    setCommentDialogVisible(false);
    setPendingTransitionTarget(null);
  };



  const handleCancel = async () => {
    if (!apiClients) return;
    const { graphClient } = apiClients;
    try {
      const response = await graphClient.post(`/arcs/${arcId}/cancel`, {
        reason: t('arc.errors.user_cancelled'),
      });

      if (response.status === 'success') {
        // Refetch stage instances to reflect cancellation
        await fetchStageInstances();

        // Notify parent component if arc data was returned
        if (response.arc) {
          onStageUpdated?.(response.arc);
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('arc.errors.cancel_failed');
      setTransitionError(errorMessage);
    }
  };

  const getHealthColor = () => {
    const colorMap: Record<string, string> = {
      on_track: '#198754',
      at_risk: '#ffc107',
      overdue: '#dc3545',
      blocked: '#fd7e14',
      attention_needed: '#0d6efd',
    };
    return colorMap[healthStatus] || '#6c757d';
  };

  const getHealthLabel = () => {
    const labelMap: Record<string, string> = {
      on_track: t('arc.health.on_track', 'On Track'),
      at_risk: t('arc.health.at_risk', 'At Risk'),
      overdue: t('arc.health.overdue', 'Overdue'),
      blocked: t('arc.health.blocked', 'Blocked'),
      attention_needed: t('arc.health.attention_needed', 'Attention'),
    };
    return labelMap[healthStatus] || healthStatus;
  };


  // Combine all loading states
  const isLoading = templateLoading || stageFetchLoading || transitionLoading;

  // Combine all error states
  const combinedError = templateError || stageFetchError || transitionError;

  if (isLoading && !arcTemplate) {
    return <div className="arc-stage-selector-loading">{t('common.loading')}</div>;
  }

  if (combinedError) {
    return <div className="arc-stage-selector-error">{combinedError}</div>;
  }

  if (!arcTemplate || !apiClients) {
    return null;
  }

  return (
    <>
      <div className="arc-stage-selector-minimal">
        {/* Use the StageDropdownAtom */}
        <StageDropdownAtom
          stages={arcTemplate.definition.stages}
          stageInstances={stageInstances}
          currentStageId={currentStageId}
          currentStageName={currentStageName}
          loading={isLoading}
          disabled={disabled}
          requireComment={requireComment}
          onTransition={handleTransition}
          onRequestComment={handleRequestComment}
          permissionCheck={permissionCheck}
        />

        <ChipAtom
          label={getHealthLabel()}
          color={getHealthColor()}
          className="health-badge"
        />

        {arcTemplate && currentStageId && (
          <ArcActionButtonMolecule
            arcTemplate={arcTemplate}
            currentStageId={currentStageId}
            onTransition={handleTransition}
            onCancel={handleCancel}
            onRequestComment={handleRequestComment}
            disabled={isLoading || disabled}
          />
        )}
      </div>

      <TransitionCommentDialogMolecule
        visible={commentDialogVisible}
        targetStageName={pendingTransitionStage ? t(pendingTransitionStage.localizationKey, pendingTransitionStage.fallbackName) : undefined}
        required={requireComment}
        onConfirm={handleCommentConfirm}
        onCancel={handleCommentCancel}
      />
    </>
  );
};

export default ArcStageSelectorMolecule;
